// PATH: src/pages/LoginPage.tsx — connexion via tc_identity (SSO écosystème).
//
// Un seul objet à l'écran : le formulaire. Le volet de marque qui occupait la
// moitié gauche vendait le produit à quelqu'un qui l'a déjà acheté — le
// personnel d'un établissement se connecte ici tous les matins et n'a besoin
// que d'entrer. La marque tient dans le bloc au-dessus de la carte.
import React, { useState } from "react";
import { Button, Form, Input, Spin, Typography, message } from "antd";
import { BankOutlined, LockOutlined, UserOutlined } from "@ant-design/icons";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const LoginPage: React.FC = () => {
  // Connexion via le contexte, jamais via useAuthApi directement : c'est lui
  // qui met `status` à jour. Sinon le garde de route verrait encore
  // « anonymous » juste après la connexion et renverrait ici — boucle.
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  // Page d'origine mémorisée par le garde, pour y revenir après connexion.
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";

  const onFinish = async (values: { login: string; password: string }) => {
    setLoading(true);
    try {
      const ok = await login(values.login.trim(), values.password);
      if (ok) {
        navigate(from, { replace: true });
      } else {
        message.error("Session non établie — réessayez.");
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) {
        message.error("Identifiants invalides");
      } else if (!err?.response) {
        // Aucune réponse : requête bloquée avant d'atteindre le serveur
        // (réseau coupé, CORS, service arrêté). Le dire, plutôt que de laisser
        // croire que les identifiants ont été refusés.
        message.error("Serveur injoignable — vérifiez votre connexion ou réessayez plus tard.");
      } else {
        message.error("Connexion impossible");
      }
    } finally {
      setLoading(false);
    }
  };

  // Session pas encore vérifiée : afficher le formulaire maintenant le ferait
  // clignoter avant la redirection d'un utilisateur déjà connecté.
  if (status === "unknown") {
    return (
      <div className="login-page" style={{ display: "grid", placeItems: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  // Session déjà ouverte : ne pas afficher le formulaire (retour arrière du
  // navigateur, ou /login saisi à la main).
  if (status === "authenticated") {
    return <Navigate to={from} replace />;
  }

  return (
    // Le fond et sa trame réglée vivent en CSS (index.css) : la texture reprend
    // le rythme d'un registre scolaire, à un contraste assez bas pour rester
    // une matière et non un motif.
    <div className="login-page">
      <div className="login-card-wrap app-fade-in">
        {/* Marque AU-DESSUS de la carte : la carte ne contient que le
            formulaire, ce qui laisse au regard un seul objet à traiter. */}
        <div className="login-brand">
          <span className="login-mark">
            <BankOutlined style={{ color: "#93B4FF", fontSize: 19 }} />
          </span>
          <Typography.Text className="login-wordmark">TilmiSchool</Typography.Text>
        </div>

        <div className="login-card">
          <Typography.Title level={3} className="login-title">
            Connexion
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="login-lede">
            Entrez avec votre compte Tilmid.
          </Typography.Paragraph>

          <Form layout="vertical" onFinish={onFinish} requiredMark={false} size="large">
            <Form.Item
              name="login"
              label="Identifiant ou email"
              rules={[{ required: true, message: "Identifiant requis" }]}
            >
              {/* autoComplete : laisse le gestionnaire de mots de passe
                  remplir le couple identifiant/mot de passe. */}
              <Input
                prefix={<UserOutlined style={{ color: "var(--app-muted)" }} />}
                autoFocus
                autoComplete="username"
                placeholder="prenom.nom"
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="Mot de passe"
              rules={[{ required: true, message: "Mot de passe requis" }]}
              style={{ marginBottom: 26 }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: "var(--app-muted)" }} />}
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{ height: 46, fontWeight: 600, fontSize: 15 }}
            >
              Se connecter
            </Button>
          </Form>
        </div>

        {/* Deux faits, pas un slogan : à quel écosystème appartient cet écran,
            et à qui il s'adresse. */}
        <Typography.Text className="login-foot">
          Écosystème Tilmid · Accès réservé au personnel de l'établissement
        </Typography.Text>
      </div>
    </div>
  );
};

export default LoginPage;
